import { useEffect, useState } from "react"
import { client } from "src/graphql"
import { GET_MY_PROFILE } from "src/graphql/queries/users"
import { buildConfigAuthorizationUrl } from "src/utils/configAuthorization"
import {
    clearPendingConfigProvisioning,
    ProvisioningProfile,
    readPendingConfigProvisioning,
    selectProvisionedHotelId,
} from "src/utils/provisioning"
import "./AuthConfig.css"

const POLL_INTERVAL_MS = 2_000
const POLL_TIMEOUT_MS = 2 * 60 * 1_000

interface MyProfileProvisioningData {
    mi_perfil?: ProvisioningProfile | null
}

type ProvisioningStatus = "polling" | "timeout" | "error"

function ConfigProvisioning(): JSX.Element {
    const [attempt, setAttempt] = useState(0)
    const [status, setStatus] = useState<ProvisioningStatus>("polling")
    const [error, setError] = useState("")
    const [provisioningRequest] = useState(() => {
        const query = new URLSearchParams(window.location.search)
        const sourceValues = query.getAll("source")
        const stateValues = query.getAll("state")
        return {
            source: sourceValues.length === 1 ? sourceValues[0] : "",
            state: stateValues.length === 1 ? stateValues[0] : "",
            stateCount: stateValues.length,
        }
    })

    useEffect(() => {
        window.history.replaceState(null, document.title, window.location.pathname)

        const sesion_id = sessionStorage.getItem("@sesion_id") || ""
        const pending = readPendingConfigProvisioning()
        const snapshotHotelIds =
            provisioningRequest.source === "stripe" && pending?.state === provisioningRequest.state
                ? pending.hotel_ids
                : provisioningRequest.source === "session" && provisioningRequest.stateCount === 0
                ? undefined
                : null
        let stopped = false
        let pollTimer: ReturnType<typeof setTimeout> | undefined = undefined

        setStatus("polling")
        setError("")

        const stopWith = (nextStatus: ProvisioningStatus, message = "") => {
            if (stopped) return
            stopped = true
            clearTimeout(pollTimer)
            clearTimeout(deadlineTimer)
            setStatus(nextStatus)
            setError(message)
        }

        const deadlineTimer = setTimeout(() => {
            stopWith("timeout")
        }, POLL_TIMEOUT_MS)

        if (snapshotHotelIds === null) {
            stopWith("error", "No pudimos verificar el retorno del pago. Vuelve a abrir el enlace desde Stripe.")
            return
        }

        const poll = async () => {
            if (stopped) return
            if (!sesion_id) {
                stopWith("error", "No encontramos el identificador de tu sesión. Inicia sesión nuevamente.")
                return
            }

            try {
                const { data } = await client.query<MyProfileProvisioningData>({
                    query: GET_MY_PROFILE,
                    fetchPolicy: "no-cache",
                    variables: { sesion_id },
                })
                if (stopped) return

                const hotelId = selectProvisionedHotelId(data?.mi_perfil, snapshotHotelIds)
                if (hotelId) {
                    const configUrl = buildConfigAuthorizationUrl("ALTA_HOTEL", hotelId)
                    if (!configUrl) {
                        setError("No se pudo generar la URL de configuración. Intenta nuevamente.")
                        pollTimer = setTimeout(poll, POLL_INTERVAL_MS)
                        return
                    }
                    stopped = true
                    clearTimeout(pollTimer)
                    clearTimeout(deadlineTimer)
                    clearPendingConfigProvisioning()
                    window.location.replace(configUrl)
                    return
                }

                pollTimer = setTimeout(poll, POLL_INTERVAL_MS)
            } catch (pollError) {
                if (stopped) return
                setError(
                    pollError instanceof Error ? pollError.message : "No pudimos consultar el estado de tu suscripción."
                )
                pollTimer = setTimeout(poll, POLL_INTERVAL_MS)
            }
        }

        poll()

        return () => {
            stopped = true
            clearTimeout(pollTimer)
            clearTimeout(deadlineTimer)
        }
    }, [attempt, provisioningRequest])

    const retry = () => setAttempt((value) => value + 1)

    return (
        <main className="auth-config-screen">
            <section className="auth-config-card" aria-live="polite">
                <img
                    className="auth-config-logo"
                    src={require("src/assets/png/logo_md.png")}
                    width={224}
                    height={40}
                    alt="EasyRoom"
                />
                {status === "polling" ? (
                    <>
                        <div className="auth-config-spinner" aria-hidden="true" />
                        <span className="auth-config-kicker">Pago recibido</span>
                        <h1>Estamos preparando tu hotel</h1>
                        <p>La activación puede tardar un par de minutos. No cierres esta ventana.</p>
                    </>
                ) : (
                    <>
                        <span className="auth-config-kicker">Activación pendiente</span>
                        <h1>Tu hotel todavía se está preparando</h1>
                        <p>
                            {status === "timeout"
                                ? "La activación está tomando más tiempo de lo esperado. Tu pago no se repetirá."
                                : error || "No pudimos confirmar la activación en este momento."}
                        </p>
                        <button className="auth-config-button" type="button" onClick={retry}>
                            Volver a comprobar
                        </button>
                    </>
                )}
            </section>
        </main>
    )
}

export default ConfigProvisioning
